import type { PromoterResponse } from '../store/promoterApi';

export type DerivedProgress = {
  reviewCompleted: boolean;
  testimonialCompleted: boolean;
  useCaseCompleted: boolean;
  firstMeetingCompleted: boolean;
};

export type PromoterLevelKey =
  | 'invited_only'
  | 'invited_promoter'
  | 'advocate_promoter'
  | 'champion_promoter'
  | 'ambassador';

export function deriveProgressFromTestimonialsAndMeetings(
  response: PromoterResponse
): DerivedProgress {
  const testimonials = response.testimonials ?? [];
  const meetings = response.meetings ?? [];

  const reviewCompleted = testimonials.some((t) => t.type === 'REVIEW');
  const testimonialCompleted = testimonials.some((t) => t.type === 'VIDEO');
  const useCaseCompleted = testimonials.some((t) => t.type === 'USE_CASE');

  const firstMeetingCompleted = meetings.some((m) => {
    const s = (m.status ?? '').toUpperCase();
    if (s === 'COMPLETED' || s === 'PARTIALLY_ATTENDED') return true;
    return !!(m.meeting_started_at ?? m.prospect_joined_at ?? m.promoter_joined_at);
  });

  return {
    reviewCompleted,
    testimonialCompleted,
    useCaseCompleted,
    firstMeetingCompleted,
  };
}

export function getPromoterLevelKey(response: PromoterResponse): PromoterLevelKey {
  const {
    reviewCompleted,
    testimonialCompleted,
    useCaseCompleted,
    firstMeetingCompleted,
  } = deriveProgressFromTestimonialsAndMeetings(response);

  // Ladder (cumulative): Review → Use Case → 1-to-1 availability (completed call) → Video Testimonial
  if (reviewCompleted && useCaseCompleted && firstMeetingCompleted && testimonialCompleted) {
    return 'ambassador';
  }
  if (reviewCompleted && useCaseCompleted && firstMeetingCompleted) {
    return 'champion_promoter';
  }
  if (reviewCompleted && useCaseCompleted) {
    return 'advocate_promoter';
  }
  if (reviewCompleted) {
    return 'invited_promoter';
  }
  return 'invited_only';
}

export const PROMOTER_LEVEL_NUMBERS: Record<PromoterLevelKey, number> = {
  invited_only: 0,
  invited_promoter: 1,
  advocate_promoter: 2,
  champion_promoter: 3,
  ambassador: 4,
};

export function getPromoterLevelNumber(response: PromoterResponse | null): number {
  if (!response) return 0;
  return PROMOTER_LEVEL_NUMBERS[getPromoterLevelKey(response)];
}

export interface LevelBadge {
  id: string;
  level: number;
  name: string;
  description: string;
  rarity: 'legendary' | 'epic' | 'rare';
}

export const PROMOTER_LEVEL_BADGES: LevelBadge[] = [
  {
    id: 'level-1',
    level: 1,
    name: 'Level 1 Promoter',
    description: 'At least one completed review',
    rarity: 'epic',
  },
  {
    id: 'level-2',
    level: 2,
    name: 'Advocate Promoter',
    description: 'Review + Use case',
    rarity: 'rare',
  },
  {
    id: 'level-3',
    level: 3,
    name: 'Champion Promoter',
    description: 'Review + Use case + 1/1 available',
    rarity: 'rare',
  },
  {
    id: 'level-4',
    level: 4,
    name: 'Ambassador',
    description: 'Review + Use case + 1/1 available + Video testimonial',
    rarity: 'legendary',
  },
];

export function getBadgesForPromoter(response: PromoterResponse | null): LevelBadge[] {
  const levelNum = getPromoterLevelNumber(response);
  if (levelNum < 1) return [];
  return PROMOTER_LEVEL_BADGES.filter((b) => b.level <= levelNum);
}
